<template>
  <div class="review">
    <div v-if="isSelectable" class="review__checkbox-section">
      <v-checkbox
        v-model="reviewSelected"
        class="review__checkbox"
      ></v-checkbox>
    </div>
    <div class="review__container">
      <div class="review__content">
        <ReviewItem
          :review="review"
          :user-actions="userActions"
          :facility="facility"
          :questions-number="item.children.length"
          @show-answers="showAnswers = !showAnswers"
        />

        <template v-if="showAnswers">
          <ReviewItem
            v-for="review in item.children"
            :key="review.id"
            :review="getNestedReviewProps(review)"
            is-nested
          />
        </template>
      </div>
      <div v-if="facility" class="review__additional-data">
        <ReviewItemFacilityData
          :name="facility.name"
          :address="facility.address"
        />

        <ReviewBoosterTagsSelector
          v-if="isCategoriesEnabled"
          ref="reviewTags"
          :tags="tags"
          :show-tags="showTags"
          :active-tags="activeTags"
          :review-id="item.id"
          :calculate-height="isFirstElement"
          @tags-generated="onTagsGenerated"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import ReviewItem from "@/components/ReviewBooster/ReviewItem";
import { FORMAT } from "@/config/date-format";
import ReviewBoosterTagsSelector from "@/components/ReviewBooster/ReviewBoosterTagsSelector.vue";
import ReviewItemFacilityData from "./ReviewItemFacilityData.vue";

export default {
  name: "ReviewBoosterReview",
  components: {
    ReviewItem,
    ReviewBoosterTagsSelector,
    ReviewItemFacilityData
  },
  inject: {
    isCategoriesEnabled: {
      default: false
    }
  },
  props: {
    item: {
      required: true,
      type: Object
    },
    isFirstElement: {
      default: false,
      type: Boolean
    },
    tags: {
      type: Array
    },
    isSelectable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showAnswers: false,
      reviewSelected: false,
      showTags: false,
      tagsRenderObserver: null
    };
  },
  computed: {
    activeTags() {
      return this.item.categories || [];
    },

    review() {
      return {
        question: this.item.question,
        comment: this.item.comment,
        rating: this.item.rating,
        date: this.formatDate(this.item.created_at),
        categories: this.item.categories
      };
    },
    userActions() {
      return {
        email: this.item.email,
        googleMapsClicked: this.item.google_maps_clicked,
        phoneNumber: this.item.phone_number,
        aiGenerated: this.item.ai_generated,
        gmbReviewMatched: this.item.gmb_review_matched
      };
    },
    facility() {
      return {
        name: this.item.facility_name,
        address: this.facilityAddress
      };
    },
    facilityAddress() {
      const storeLabel = this.item.store_code
        ? ` | ${this.$t("message.common.storeCode")}: ${this.item.store_code}`
        : "";
      return `${this.item.formatted_address}${storeLabel}`;
    }
  },
  created() {
    if (this.isFirstElement) {
      this.renderTags();
    }
  },
  methods: {
    renderTags() {
      this.showTags = true;
    },
    formatDate(date) {
      return moment(date).format(FORMAT.DD_MM_YYYY);
    },
    getNestedReviewProps({ question, comment, rating }) {
      return {
        question,
        comment,
        rating
      };
    },

    async emitTagsHeightChanged() {
      this.$emit("tags-height-changed", this.$refs.reviewTags.$el.offsetHeight);
    },

    setupIntersectionObserver() {
      this.tagsRenderObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            this.renderTags();
            this.tagsRenderObserver.disconnect();
          }
        },
        {
          root: null,
          rootMargin: "200px",
          threshold: 0.1
        }
      );

      this.$nextTick(() => {
        this.tagsRenderObserver.observe(this.$refs.reviewTags.$el);
      });
    },
    async onTagsGenerated(height) {
      if (!height) {
        this.setupIntersectionObserver();
        return;
      }

      await this.$nextTick();
      this.$emit("tags-height-changed", height);
    }
  },

  beforeDestroy() {
    if (this.tagsRenderObserver) {
      this.tagsRenderObserver.disconnect();
    }
  }
};
</script>

<style lang="scss" scoped>
.review {
  display: flex;

  &__content {
    flex-grow: 1;
    border-right: 1px solid $gray-200;
    margin-right: $spacing * 4;
  }

  &__container {
    display: flex;
    flex-grow: 1;
    overflow: hidden;
  }

  &__checkbox-section {
    flex-shrink: 0;
    margin-right: $spacing * 2;
    display: flex;

    &:after {
      content: "";
      display: block;
      height: 100%;
      width: 3px;
      background: $gray-700;
      margin-left: $spacing * 2;
      border-radius: 3px;
    }
  }

  &__checkbox {
    margin-top: $spacing * 0.5;
    padding: 0;
    border-color: red;
    ::v-deep .v-input--selection-controls__input {
      margin: 0;
    }
  }

  &__additional-data {
    width: 400px;
    flex-shrink: 0;
  }
}
</style>
